import React, { useState, useMemo, useCallback } from "react";
import { IconButton, Typography, Box, Tooltip, Button } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";
import styles from "./outcome-finalizer.module.scss";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { recordAgentAction } from "../../../store/agentSlice";
import MarkdownRenderer from "../../MarkdownRenderer";
import DocumentCard from "../shared/DocumentCard";
import DocumentToolbar from "../shared/DocumentToolbar";
import SourceVerificationSlider from "../shared/SourceVerificationSlider";

interface Region {
  page_number: number;
  polygon: number[];
  page_width: number;
  page_height: number;
  unit: string;
}

interface SourceLocation {
  document_url?: string;
  page_number: number;
  polygon: number[];
  coordinate_system: string;
  page_width: number;
  page_height: number;
  unit: string;
  regions?: Region[];
}

interface ClauseReference {
  clause_number?: string;
  section_reference: string;
  clause_text_snippet: string;
  source_location?: SourceLocation;
  is_verified?: boolean;
}

interface ContractDetails {
  contract_type: string;
  contracting_parties: string[];
  agreement_date: string;
}

interface ActionItem {
  action: string;
  reason: string;
  responsible_party?: string;
  clause_reference?: string;
}

interface DocumentOutcome {
  document_name: string;
  contract_details: ContractDetails;
  is_coc_clause_present: boolean;
  clause_reference: ClauseReference[];
  extracted_threshold: string;
  transaction_percentage: string;
  trigger_result: string;
  reason: string;
  action_required: ActionItem[] | string | string[];
  consequence_summary: string;
  consequence_severity?: string;
  risk_tier: string;
  human_review: string;
  document_url?: string;
  is_verified?: boolean;
}

interface ParsedContent {
  finalAnswer?: {
    documents?: DocumentOutcome[];
  };
  question?: string;
}

interface OutcomeFinalizerAgentProps {
  parsedContent: ParsedContent;
}

const getRiskTierColor = (riskTier: string): string => {
  const normalized = riskTier?.toLowerCase();
  if (normalized === "green" || normalized === "low") return "green";
  if (normalized === "red" || normalized === "high") return "red";
  if (normalized === "yellow" || normalized === "medium") return "medium";
  return "medium";
};

const formatContractingParties = (parties: string[]): string => {
  if (!parties || parties.length === 0) return "N/A";
  if (parties.length === 1) return parties[0];
  if (parties.length === 2) return `${parties[0]} and ${parties[1]}`;
  return `${parties.slice(0, -1).join(", ")}, and ${parties[parties.length - 1]}`;
};

export default function OutcomeFinalizerAgent({
  parsedContent,
}: OutcomeFinalizerAgentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const numericAgentId = currentAgent?.id || 11;
  const documents = parsedContent?.finalAnswer?.documents || [];
  const [sliderData, setSliderData] = useState<{
    documents: Array<{
      documentName: string;
      documentUrl?: string;
      clauses: ClauseReference[];
      documentIndex: number;
    }>;
    initialDocumentIndex: number;
  } | null>(null);
  const [sortBy, setSortBy] = useState("document-order");
  const [filterBy, setFilterBy] = useState("all");
  const [riskTierFilter, setRiskTierFilter] = useState("all");
  const [expandedDocs, setExpandedDocs] = useState<Record<number, boolean>>({});

  // Sorting and filtering logic
  const sortedAndFilteredDocuments = useMemo(() => {
    let filtered = [...documents];

    // Apply verification filter
    if (filterBy !== "all") {
      filtered = filtered.filter((doc) => {
        switch (filterBy) {
          case "verified":
            return doc.is_verified === true;
          case "not-verified":
            return doc.is_verified === false || doc.is_verified === undefined;
          default:
            return true;
        }
      });
    }

    // Apply risk tier filter
    if (riskTierFilter !== "all") {
      filtered = filtered.filter((doc) => {
        const normalizedRiskTier = doc.risk_tier?.toLowerCase();
        switch (riskTierFilter) {
          case "high":
            return (
              normalizedRiskTier === "high" || normalizedRiskTier === "red"
            );
          case "medium":
            return (
              normalizedRiskTier === "medium" || normalizedRiskTier === "yellow"
            );
          case "low":
            return (
              normalizedRiskTier === "low" || normalizedRiskTier === "green"
            );
          default:
            return true;
        }
      });
    }

    // Apply sorting
    const sorted = [...filtered];
    switch (sortBy) {
      case "risk-high":
        sorted.sort((a, b) => {
          const riskOrder: Record<string, number> = {
            high: 3,
            red: 3,
            medium: 2,
            yellow: 2,
            low: 1,
            green: 1,
          };
          const aRisk = riskOrder[a.risk_tier?.toLowerCase() || ""] || 0;
          const bRisk = riskOrder[b.risk_tier?.toLowerCase() || ""] || 0;
          return bRisk - aRisk;
        });
        break;
      case "risk-low":
        sorted.sort((a, b) => {
          const riskOrder: Record<string, number> = {
            high: 3,
            red: 3,
            medium: 2,
            yellow: 2,
            low: 1,
            green: 1,
          };
          const aRisk = riskOrder[a.risk_tier?.toLowerCase() || ""] || 0;
          const bRisk = riskOrder[b.risk_tier?.toLowerCase() || ""] || 0;
          return aRisk - bRisk;
        });
        break;
      default:
        break;
    }

    return sorted;
  }, [documents, sortBy, filterBy, riskTierFilter]);

  const handleClauseClick = (clickedDocIndex: number) => {
    // Prepare all documents with clauses for the slider
    // Note: source_location is at CLAUSE level only
    const allDocuments = sortedAndFilteredDocuments.map((doc, index) => ({
      documentName: doc.document_name,
      documentUrl: doc.document_url,
      clauses: doc.clause_reference || [], // Each clause has its own source_location
      documentIndex: index,
    }));

    // Find the clicked document in the full documents array
    const clickedDoc = documents[clickedDocIndex];
    const sliderDocIndex = allDocuments.findIndex(
      (d) => d.documentName === clickedDoc.document_name,
    );

    setSliderData({
      documents: allDocuments,
      initialDocumentIndex: sliderDocIndex >= 0 ? sliderDocIndex : 0,
    });
  };

  const handleCloseSlider = () => {
    setSliderData(null);
  };

  const handleResetFilters = useCallback(() => {
    setFilterBy("all");
    setRiskTierFilter("all");
  }, []);

  const handleExportExcel = () => {
    try {
      // Prepare headers (matching table columns exactly)
      const headers = [
        "Contract Details",
        "Identify CoC",
        "Clause Reference",
        "Identify CoC Trigger",
        "Action Items",
        "Consequence of not adhering to CoC requirements",
        "Risk Tier",
      ];

      // Prepare data for Excel export
      const excelData: any[][] = [];

      // Add header row
      excelData.push(headers);

      // Add empty row after header
      excelData.push([]);

      documents.forEach((doc) => {
        // Format contract details (Type, Parties, Date)
        const contractDetails = doc.contract_details
          ? `Type: ${doc.contract_details.contract_type}\nParties: ${formatContractingParties(doc.contract_details.contracting_parties)}\nDate: ${doc.contract_details.agreement_date}`
          : doc.document_name;

        // Format clause references (numbers only for main row)
        const clauseRefs =
          doc.clause_reference
            ?.map((ref) => ref.clause_number)
            .filter(Boolean)
            .join(", ") || "N/A";

        // Format action_required for Excel
        let actionItemsText = "N/A";
        if (Array.isArray(doc.action_required)) {
          actionItemsText = doc.action_required.map((item, idx) => {
            if (typeof item === 'object' && item !== null && 'action' in item) {
              let text = `${idx + 1}. Action: ${item.action}\n   Reason: ${item.reason}`;
              if (item.responsible_party) {
                text += `\n   Responsible Party: ${item.responsible_party}`;
              }
              if (item.clause_reference) {
                text += `\n   Clause Reference: ${item.clause_reference}`;
              }
              return text;
            }
            return `${idx + 1}. ${item}`;
          }).join("\n\n");
        } else if (doc.action_required) {
          actionItemsText = doc.action_required;
        }

        // Main document row
        const mainRow = [
          contractDetails,
          doc.is_coc_clause_present ? "Yes" : "No",
          clauseRefs,
          doc.trigger_result || "N/A",
          actionItemsText,
          doc.consequence_summary || "N/A",
          doc.risk_tier || "N/A",
        ];

        excelData.push(mainRow);

        // Add clause detail rows if available
        if (doc.clause_reference && doc.clause_reference.length > 0) {
          doc.clause_reference.forEach((clause) => {
            const clauseDetailRow = [
              `  └─ Clause ${clause.clause_number || ""}`,
              "",
              `Section: ${clause.section_reference || "N/A"}\n\n"${clause.clause_text_snippet || "N/A"}"`,
              "",
              "",
              "",
              "",
              "",
            ];
            excelData.push(clauseDetailRow);
          });
        }

        // Add empty row for separation between documents
        excelData.push([]);
      });

      // Create worksheet from array of arrays
      const ws = XLSX.utils.aoa_to_sheet(excelData);

      // Set column widths for better readability (matching 7 table columns)
      const columnWidths = [
        { wch: 50 }, // Contract Details (Type, Parties, Date combined)
        { wch: 15 }, // Identify CoC
        { wch: 30 }, // Clause Reference
        { wch: 20 }, // Identify CoC Trigger
        { wch: 50 }, // Action Items
        { wch: 50 }, // Consequence of not adhering to CoC requirements
        { wch: 12 }, // Risk Tier
      ];
      ws["!cols"] = columnWidths;

      // Enable text wrapping for all cells
      const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[cellAddress]) continue;
          if (!ws[cellAddress].s) ws[cellAddress].s = {};
          ws[cellAddress].s.alignment = { wrapText: true, vertical: "top" };
        }
      }

      // Create workbook and add worksheet
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "CoC Analysis");

      // Generate file name
      const timestamp = new Date().toISOString().split("T")[0];
      const fileName = `coc-final-outcome-${timestamp}.xlsx`;

      // Record action
      dispatch(
        recordAgentAction({
          agentId: numericAgentId,
          actionType: "download",
          content: JSON.stringify({
            finalAnswer: {
              documents: documents,
            },
          }),
        }),
      );

      // Download file
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error("Error exporting Excel:", error);
    }
  };

  const handleDownloadPDF = () => {
    // Deprecated - replaced with Excel export
    handleExportExcel();
  };

  if (documents.length === 0) {
    return (
      <div className={styles.container}>
        <p>No outcome data available.</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerSection}>
        <h2 className={styles.title}>
          Change of Control Analysis - Final Report
        </h2>
      </div>

      <DocumentToolbar
        sortBy={sortBy}
        onSortChange={setSortBy}
        filterBy={filterBy}
        onFilterChange={setFilterBy}
        riskTierFilter={riskTierFilter}
        onRiskTierFilterChange={setRiskTierFilter}
        onResetFilters={handleResetFilters}
        instructionText="Final analysis complete. Review the documents below and export to Excel if needed."
      />

      <div className={styles.documentsWrapper}>
        {sortedAndFilteredDocuments.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              px: 3,
              color: "#666",
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, color: "#999" }}>
              No documents match your filters
            </Typography>
            <Typography variant="body2" sx={{ color: "#999" }}>
              Try adjusting your verification or risk tier filters to see more
              results.
            </Typography>
          </Box>
        ) : (
          sortedAndFilteredDocuments.map((doc, index) => (
            <DocumentCard
              key={index}
              documentName={doc.document_name}
              riskTier={doc.risk_tier}
              isExpanded={expandedDocs[index] !== false}
              onToggleExpand={() =>
                setExpandedDocs((prev) => ({ ...prev, [index]: !prev[index] }))
              }
            >
              <div className={styles.documentDetailsTable}>
                <table className={styles.detailsTable}>
                  <thead>
                    <tr>
                      <th>CONTRACT DETAILS</th>
                      <th>IDENTIFY COC</th>
                      <th>CLAUSE REFERENCE</th>
                      <th>IDENTIFY COC TRIGGER</th>
                      <th>ACTION ITEMS</th>
                      <th>CONSEQUENCE OF NOT ADHERING TO COC REQUIREMENTS</th>
                      <th>RISK TIER</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className={styles.contractDetailsCell}>
                        {doc.contract_details ? (
                          <div className={styles.contractInfo}>
                            <div>
                              <strong>Type:</strong>{" "}
                              <span>{doc.contract_details.contract_type}</span>
                            </div>
                            <div>
                              <strong>Parties:</strong>{" "}
                              <span>
                                {formatContractingParties(
                                  doc.contract_details.contracting_parties,
                                )}
                              </span>
                            </div>
                            <div>
                              <strong>Date:</strong>{" "}
                              <span>{doc.contract_details.agreement_date}</span>
                            </div>
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td className={styles.centered}>
                        <span
                          className={
                            doc.is_coc_clause_present ? styles.yes : styles.no
                          }
                        >
                          {doc.is_coc_clause_present ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className={styles.clauseCell}>
                        {doc.clause_reference &&
                        doc.clause_reference.length > 0 ? (
                          <div className={styles.clauseWithButton}>
                            <span>
                              {doc.clause_reference
                                .map((ref) => ref.clause_number)
                                .filter(Boolean)
                                .join(", ") || "View"}
                            </span>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleClauseClick(index)}
                              className={styles.verifySourceButton}
                            >
                              Verify source
                            </Button>
                          </div>
                        ) : (
                          <span style={{ color: "#999", fontStyle: "italic" }}>
                            Could not verify this clause reference
                          </span>
                        )}
                      </td>
                      <td className={styles.centered}>
                        <Tooltip
                          title={doc.reason || "N/A"}
                          arrow
                          placement="top"
                        >
                          <span className={styles.triggerBadge}>
                            {doc.trigger_result}
                          </span>
                        </Tooltip>
                      </td>
                      <td className={styles.actionCell}>
                        {Array.isArray(doc.action_required) ? (
                          <ul>
                            {doc.action_required.map((item, idx) => {
                              // Check if it's the new object format with action and reason
                              if (typeof item === 'object' && item !== null && 'action' in item) {
                                return (
                                  <li key={idx}>
                                    <strong>Action:</strong> {item.action}
                                    <br />
                                    <strong>Reason:</strong> {item.reason}
                                    {item.responsible_party && (
                                      <>
                                        <br />
                                        <strong>Responsible Party:</strong> {item.responsible_party}
                                      </>
                                    )}
                                    {item.clause_reference && (
                                      <>
                                        <br />
                                        <strong>Clause Reference:</strong> {item.clause_reference}
                                      </>
                                    )}
                                  </li>
                                );
                              }
                              // Fallback for legacy string format
                              return <li key={idx}>{item}</li>;
                            })}
                          </ul>
                        ) : (
                          doc.action_required || "N/A"
                        )}
                      </td>
                      <td
                        className={styles.consequenceCell}
                        style={{ fontSize: "14px", lineHeight: 1.5 }}
                      >
                        {doc.consequence_summary || "N/A"}
                        {doc.consequence_severity && (
                          <div style={{ marginTop: "8px", fontSize: "12px" }}>
                            Consequence Severity: {doc.consequence_severity}
                          </div>
                        )}
                      </td>
                      <td className={styles.centered}>
                        <span
                          className={`${styles.riskBadge} ${
                            styles[getRiskTierColor(doc.risk_tier)]
                          }`}
                        ></span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </DocumentCard>
          ))
        )}
      </div>

      {/* Source Verification Slider */}
      {sliderData && (
        <SourceVerificationSlider
          open={Boolean(sliderData)}
          onClose={handleCloseSlider}
          documents={sliderData.documents}
          initialDocumentIndex={sliderData.initialDocumentIndex}
          mode="readonly"
        />
      )}

      {/* Export Section */}
      <div className={styles.downloadSection}>
        <div className={styles.downloadContent} style={{ cursor: "pointer" }}>
          <FileDownloadIcon className={styles.downloadSectionIcon} />
          <div className={styles.downloadText}>
            <Typography className={styles.downloadTitle}>
              Export to Excel
            </Typography>
            <Typography className={styles.downloadDescription}>
              Get a comprehensive Excel report of your Change of Control
              analysis
            </Typography>
          </div>
        </div>
        <Tooltip title="Export to Excel" arrow>
          <IconButton
            className={styles.downloadButton}
            onClick={handleExportExcel}
            aria-label="Export Excel Report"
          >
            <FileDownloadIcon className={styles.downloadButtonIcon} />
            <span>Export Excel</span>
          </IconButton>
        </Tooltip>
      </div>

      {/* Question Section */}
      {parsedContent?.question && (
        <Box className={styles.questionBox}>
          <Box className={styles.questionContent}>
            <MarkdownRenderer content={parsedContent.question} />
          </Box>
        </Box>
      )}
    </div>
  );
}
